import { Injectable } from '@angular/core';
import { ServiceGridItemObject } from '@it2go/types';
import {
    PersonFolderGridItemObject,
} from '@libs/types/src/services/object-type/person/person.object';
import { WorkplaceHoursFrequencyEnum } from '@libs/types/src/services/enum/workplace-hours-frequency.enum';
import {
    UserWorkplaceHoursObject,
} from '@libs/types/src/user/object-type/workplace/user-workplace-hours.object';
import { UserWorkplaceObject } from '@libs/types/src/user/object-type/workplace/user-workplace.object';
import {
    CalendarAvailabilityInterval,
    CalendarResourceWorkingHours,
} from '../../components/kit/calendar/calendar.types';

export interface CalendarWorkingHoursEmployment {
    serviceId: number;
    dateFrom?: string | null;
    dateTo?: string | null;
}

export interface CalendarWorkingHoursResource {
    id: string | number;
    folders?: readonly PersonFolderGridItemObject[] | null;
    workplaces?: readonly UserWorkplaceObject[] | null;
    employments?: readonly CalendarWorkingHoursEmployment[] | null;
}

export interface CalendarWorkingHoursResolveInput {
    date: Date;
    resources: readonly CalendarWorkingHoursResource[];
    services: readonly ServiceGridItemObject[];
    selectedServiceIds?: readonly number[] | null;
    defaultStartMinutes?: number;
    defaultEndMinutes?: number;
}

interface WorkplaceResolution {
    configured: boolean;
    intervals: CalendarAvailabilityInterval[];
}

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_START_MINUTES = 8 * 60;
const DEFAULT_END_MINUTES = 16 * 60;

@Injectable({ providedIn: 'root' })
export class CalendarWorkingHoursResolver {

    public resolve(input: CalendarWorkingHoursResolveInput): CalendarResourceWorkingHours[] {
        const selectedServiceIds = input.selectedServiceIds?.length
            ? [...new Set(input.selectedServiceIds)]
            : input.services.map((service) => service.id);
        const services = new Map(input.services.map((service) => [service.id, service]));

        return input.resources.map((resource) => {
            const resourceId = String(resource.id);

            if (typeof resource.id === 'string') {
                const workplaceResolution = this.resolveUserWorkplaces(resource, input.date);
                if (workplaceResolution.configured) {
                    return {
                        resourceId,
                        intervals: this.mergeIntervals(workplaceResolution.intervals),
                    };
                }
            }

            const resourceServiceIds = typeof resource.id === 'number'
                ? this.resolvePersonServiceIds(resource, selectedServiceIds)
                : this.resolveUserServiceIds(resource, selectedServiceIds, input.date);
            const serviceIntervals = resourceServiceIds.flatMap((serviceId) => (
                this.resolveServiceIntervals(services.get(serviceId), serviceId)
            ));

            if (serviceIntervals.length > 0) {
                return {
                    resourceId,
                    intervals: this.mergeIntervals(serviceIntervals),
                    fallbackSource: 'service',
                };
            }

            return {
                resourceId,
                intervals: [{
                    startMinutes: input.defaultStartMinutes ?? DEFAULT_START_MINUTES,
                    endMinutes: input.defaultEndMinutes ?? DEFAULT_END_MINUTES,
                    source: 'default',
                }],
                fallbackSource: 'default',
            };
        });
    }

    private resolveUserServiceIds(
        resource: CalendarWorkingHoursResource,
        selectedServiceIds: readonly number[],
        date: Date,
    ): number[] {
        const selected = new Set(selectedServiceIds);
        return [...new Set((resource.employments || [])
            .filter((employment) => (
                selected.has(employment.serviceId)
                && this.isDateRangeValid(employment.dateFrom, employment.dateTo, date)
            ))
            .map((employment) => employment.serviceId))];
    }

    private resolvePersonServiceIds(
        resource: CalendarWorkingHoursResource,
        selectedServiceIds: readonly number[],
    ): number[] {
        const folders = this.readFolders(resource);
        if (folders.length === 0) return [...selectedServiceIds];

        const selected = new Set(selectedServiceIds);
        return [...new Set(folders
            .filter((folder) => !folder.deletedAt && selected.has(folder.serviceId))
            .map((folder) => folder.serviceId))];
    }

    private resolveServiceIntervals(
        service: ServiceGridItemObject | undefined,
        serviceId: number,
    ): CalendarAvailabilityInterval[] {
        if (!service) return [];

        return (service.timeRanges || [])
            .filter((range) => range.active)
            .flatMap((range) => this.splitDailyInterval(
                this.timeToMinutes(range.from, 0),
                this.timeToMinutes(range.to, MINUTES_PER_DAY),
            ))
            .map((interval) => ({
                ...interval,
                source: 'service' as const,
                serviceIds: [serviceId],
            }));
    }

    private resolveUserWorkplaces(resource: CalendarWorkingHoursResource, date: Date): WorkplaceResolution {
        const workplaces = this.readWorkplaces(resource).filter((workplace) => workplace.active);
        const previousDate = this.shiftDate(date, -1);
        const configured = workplaces.some((workplace) => this.isWorkplaceValid(workplace, date));
        const intervals = workplaces.flatMap((workplace) => {
            const workplaceMetadata = {
                source: 'user-workplace' as const,
                workplaceIds: [workplace.id],
                workplaceNames: [workplace.name],
            };
            const currentIntervals = this.isWorkplaceValid(workplace, date)
                ? workplace.hours
                    .filter((hours) => this.matchesWorkplaceHours(hours, date, workplace.validFrom))
                    .flatMap((hours) => this.currentDayIntervals(hours))
                    .map((interval) => ({ ...interval, ...workplaceMetadata }))
                : [];
            const previousDayTails = this.isWorkplaceValid(workplace, previousDate)
                ? workplace.hours
                    .filter((hours) => this.matchesWorkplaceHours(hours, previousDate, workplace.validFrom))
                    .flatMap((hours) => this.nextDayTail(hours))
                    .map((interval) => ({ ...interval, ...workplaceMetadata }))
                : [];

            return [...currentIntervals, ...previousDayTails];
        });

        return { configured, intervals };
    }

    private currentDayIntervals(hours: UserWorkplaceHoursObject): Array<{
        startMinutes: number;
        endMinutes: number;
    }> {
        const startMinutes = this.timeToMinutes(hours.from, 0);
        const endMinutes = this.timeToMinutes(hours.to, MINUTES_PER_DAY);
        if (startMinutes === endMinutes) {
            return [{ startMinutes: 0, endMinutes: MINUTES_PER_DAY }];
        }
        if (endMinutes < startMinutes) {
            return [{ startMinutes, endMinutes: MINUTES_PER_DAY }];
        }
        return [{ startMinutes, endMinutes }];
    }

    private nextDayTail(hours: UserWorkplaceHoursObject): Array<{
        startMinutes: number;
        endMinutes: number;
    }> {
        const startMinutes = this.timeToMinutes(hours.from, 0);
        const endMinutes = this.timeToMinutes(hours.to, MINUTES_PER_DAY);
        return endMinutes < startMinutes
            ? [{ startMinutes: 0, endMinutes }]
            : [];
    }

    private matchesWorkplaceHours(
        hours: UserWorkplaceHoursObject,
        date: Date,
        validFrom: string | null,
    ): boolean {
        if (hours.day !== date.getDay()) return false;

        const anchor = this.safeDate(validFrom) ?? date;
        const weeksPassed = this.calendarDaysBetween(
            this.startOfWeek(anchor),
            this.startOfWeek(date),
        ) / 7;
        const monthsPassed = (date.getFullYear() - anchor.getFullYear()) * 12
            + date.getMonth() - anchor.getMonth();
        const weekOfMonth = Math.floor(
            (date.getDate() - 1 + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7,
        ) + 1;

        switch (hours.type) {
            case WorkplaceHoursFrequencyEnum.EVERY_1_WEEK:
                return true;
            case WorkplaceHoursFrequencyEnum.EVERY_2_WEEK:
                return weeksPassed >= 0 && weeksPassed % 2 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_3_WEEK:
                return weeksPassed >= 0 && weeksPassed % 3 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_4_WEEK:
                return weeksPassed >= 0 && weeksPassed % 4 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_1st_WEEK_IN_MONTH:
                return weekOfMonth === 1;
            case WorkplaceHoursFrequencyEnum.EVERY_2nd_WEEK_IN_MONTH:
                return weekOfMonth === 2;
            case WorkplaceHoursFrequencyEnum.EVERY_3rd_WEEK_IN_MONTH:
                return weekOfMonth === 3;
            case WorkplaceHoursFrequencyEnum.EVERY_4th_WEEK_IN_MONTH:
                return weekOfMonth === 4;
            case WorkplaceHoursFrequencyEnum.EVERY_1st_WEEK_IN_EVERY_2nd_MONTH:
                return weekOfMonth === 1 && monthsPassed >= 0 && monthsPassed % 2 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_2nd_WEEK_IN_EVERY_2nd_MONTH:
                return weekOfMonth === 2 && monthsPassed >= 0 && monthsPassed % 2 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_3rd_WEEK_IN_EVERY_2nd_MONTH:
                return weekOfMonth === 3 && monthsPassed >= 0 && monthsPassed % 2 === 0;
            case WorkplaceHoursFrequencyEnum.EVERY_4th_WEEK_IN_EVERY_2nd_MONTH:
                return weekOfMonth === 4 && monthsPassed >= 0 && monthsPassed % 2 === 0;
            default:
                return false;
        }
    }

    private splitDailyInterval(
        startMinutes: number,
        endMinutes: number,
    ): Array<{ startMinutes: number; endMinutes: number }> {
        if (startMinutes === endMinutes) {
            return [{ startMinutes: 0, endMinutes: MINUTES_PER_DAY }];
        }
        if (endMinutes < startMinutes) {
            return [
                { startMinutes, endMinutes: MINUTES_PER_DAY },
                { startMinutes: 0, endMinutes },
            ];
        }
        return [{ startMinutes, endMinutes }];
    }

    private mergeIntervals(intervals: CalendarAvailabilityInterval[]): CalendarAvailabilityInterval[] {
        return [...intervals]
            .filter((interval) => interval.endMinutes > interval.startMinutes)
            .sort((left, right) => left.startMinutes - right.startMinutes)
            .reduce<CalendarAvailabilityInterval[]>((result, interval) => {
                const previous = result.at(-1);
                if (!previous || previous.endMinutes < interval.startMinutes || previous.source !== interval.source) {
                    result.push({ ...interval });
                    return result;
                }

                previous.endMinutes = Math.max(previous.endMinutes, interval.endMinutes);
                previous.serviceIds = this.mergeValues(previous.serviceIds, interval.serviceIds);
                previous.workplaceIds = this.mergeValues(previous.workplaceIds, interval.workplaceIds);
                previous.workplaceNames = this.mergeValues(previous.workplaceNames, interval.workplaceNames);
                return result;
            }, []);
    }

    private mergeValues<T>(left?: T[], right?: T[]): T[] | undefined {
        if (!left?.length && !right?.length) return undefined;
        return [...new Set([...(left || []), ...(right || [])])];
    }

    private readFolders(resource: CalendarWorkingHoursResource): readonly PersonFolderGridItemObject[] {
        return resource.folders || [];
    }

    private readWorkplaces(resource: CalendarWorkingHoursResource): readonly UserWorkplaceObject[] {
        return resource.workplaces || [];
    }

    private isWorkplaceValid(workplace: UserWorkplaceObject, date: Date): boolean {
        return this.isDateRangeValid(workplace.validFrom, workplace.validTo, date);
    }

    private isDateRangeValid(
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        date: Date,
    ): boolean {
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
        const validFrom = this.safeDate(dateFrom);
        const validTo = this.safeDate(dateTo);
        return (!validFrom || validFrom <= dayEnd) && (!validTo || validTo >= dayStart);
    }

    private timeToMinutes(value: string | null | undefined, fallback: number): number {
        if (!value) return fallback;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return fallback;
        return date.getHours() * 60 + date.getMinutes();
    }

    private safeDate(value: string | null | undefined): Date | null {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private shiftDate(date: Date, days: number): Date {
        const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        result.setDate(result.getDate() + days);
        return result;
    }

    private startOfWeek(date: Date): Date {
        const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        result.setDate(result.getDate() - result.getDay());
        return result;
    }

    private calendarDaysBetween(from: Date, to: Date): number {
        const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
        const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
        return Math.floor((toUtc - fromUtc) / 86_400_000);
    }
}
